import { useCallback, useMemo } from "react";

import { useHistory } from "react-router-dom";

import {
  type TableColumn,
  type TableProps,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import {
  useGetCryptoAssets,
  useGetCryptoTransfers,
} from "~/v2/api/hooks/useGetCrypto";
import type { CryptoTransfer } from "~/v2/api/rest/cryptoWallet";
import { Path } from "~/v2/path";
import { createAssetMap } from "../util";
import { createTransfersColumns } from "./transfersColumns";

const TRANSFERS_TABLE_ROW_LIMIT = 5;

export interface UseTransfersTableControllerProps {
  accountId: string;
  isLoading: boolean;
  isFetchingEnabled: boolean;
}

export interface TransfersTableControllerReturn {
  transfers: {
    tableProps: TableProps<CryptoTransfer>;
    isLoading: boolean;
    refetch: () => void;
    onViewAllClicked: () => void;
  };
}

export const useTransfersTableController = ({
  accountId,
  isLoading,
  isFetchingEnabled,
}: UseTransfersTableControllerProps): TransfersTableControllerReturn => {
  const history = useHistory();

  const assets = useGetCryptoAssets({
    accountId,
    options: {
      enabled: isFetchingEnabled,
    },
  });

  const transfers = useGetCryptoTransfers({
    accountId,
    options: {
      enabled: isFetchingEnabled,
    },
  });

  const onTransferTransactionClicked = useCallback(
    (transferLink: string) => () =>
      window.open(transferLink, "_blank", "noopener,noreferrer"),
    [],
  );

  const assetMap = useMemo(
    () => createAssetMap(assets.data ?? []),
    [assets.data],
  );

  const transfersTableRows = useMemo(
    () =>
      [...(transfers?.data ?? [])]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, TRANSFERS_TABLE_ROW_LIMIT),
    [transfers.data],
  );

  const transfersTableColumns: TableColumn<CryptoTransfer>[] = useMemo(
    () => createTransfersColumns({ assetMap, onTransferTransactionClicked }),
    [assetMap, onTransferTransactionClicked],
  );

  const transfersTableProps = useDataGrid({
    rows: transfersTableRows,
    columns: transfersTableColumns,
  });

  const onViewAllTransfersClicked = () =>
    history.push({
      pathname: Path.ROUTE_FUNDING_HISTORY,
      search: "?tab=crypto",
    });

  return {
    transfers: {
      tableProps: transfersTableProps,
      isLoading: transfers.isLoading || isLoading,
      refetch: transfers.refetch,
      onViewAllClicked: onViewAllTransfersClicked,
    },
  };
};
